import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import CartaoBoletoUnico from './CartaoBoletoUnico';
import { derivarStatusBoleto } from '../../src/lib/statusBoleto';
import {
    valorCobradoFatura,
    normalizeMesReferencia,
    mesReferenciaParaOrdinal,
    economiaDaFatura,
    custoComVigor,
    custoSemVigor,
    formatWhatsapp,
    brl,
    dataBr,
} from './format';
import PagamentoModal, { FaturaPagamento, BoletoQuitacao } from './PagamentoModal';
import {
    FileText,
    Headset,
    Loader2,
    ChevronRight,
    Home,
    CalendarClock,
    BellRing,
    Gift,
    BookOpen,
    MessageCircle,
    BadgePercent,
    Megaphone,
} from 'lucide-react';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';
import ComunicadoMarcaModal, { type ComunicadoMarca } from './marca/ComunicadoMarcaModal';
import { CATEGORIA_COMUNICADO, linkWhatsapp } from './marca/marcaPortal';

// Portal do Consumidor — home logada (rota /portal-consumidor/inicio), no estilo
// "app de energia por assinatura": hero com a economia do mês/acumulada e o gráfico
// gastos com × sem Vigor, equivalência ambiental, faturas pendentes com botão Pagar
// (PIX/boleto) e as unidades do consumidor. Todos os números vêm dos campos oficiais
// persistidos em faturas (economia, custo_s_vigor, total_com_vigor — ver
// economiaDaFatura em format.ts); a RLS já limita tudo ao CPF do usuário.

interface FaturaHome extends FaturaPagamento {
    id: number;
    status_boleto_vigor: string | null;
    economia?: number | null;
    custo_s_vigor?: number | null;
    total_com_vigor?: number | null;
    consumo_ativo_kwh?: number | null;
    created_at: string;
}

interface ParcelamentoHome {
    id: string;
    descricao: string | null;
    valor_total: number | null;
    numero_parcelas: number;
    status: string;
    parcelamento_parcelas: { status: string }[];
}

interface UcHome {
    id: number;
    numero_uc: string;
    endereco?: string | null;
    logradouro?: string | null;
    numero?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    situacao?: string | null;
    desconto_tarifa?: number | null;
    concessionaria?: string | null;
}

const enderecoDaUc = (uc: UcHome): string => {
    const partes = [
        [uc.logradouro, uc.numero].filter(Boolean).join(', '),
        uc.bairro,
        [uc.cidade, uc.estado].filter(Boolean).join('/'),
    ].filter((p) => p && p.trim().length > 0);
    if (partes.length > 0) return partes.join(' - ');
    // endereco legado pode vir malformado (', - , /') quando os campos estruturados estão vazios
    if (uc.endereco && /[a-zA-Z0-9]/.test(uc.endereco)) return uc.endereco;
    return 'Endereço não cadastrado';
};

// Status DERIVADO (src/lib/statusBoleto) em que existe boleto para o cliente pagar.
// Mesma correção de PortalConsumidorFaturas: pelo campo cru, um ciclo sem
// compensação (valor 0) entrava como pendência de R$ 0,00 no "a pagar".
const STATUS_PENDENTES = ['Em aberto', 'Vencido'];

// ── Gráfico "gastos com × sem Vigor" (2 séries, hero escuro) ─────────────────────
// Cores: branco (com Vigor) e laranja da marca #F6A937 (sem Vigor) — validadas p/
// CVD (ΔE 70) e contraste (≥3:1) sobre a superfície verde-escura; identidade nunca
// é só cor (legenda com rótulo + tooltip com valores).

// Branco é a linha "com" em qualquer marca; a "sem" e a superfície vêm da marca
// (marca.grafico — na Gran o laranja sumiria no fundo laranja).
const COR_COM = '#FFFFFF';

interface PontoGrafico {
    mes: string; // 'JAN/2026'
    com: number;
    sem: number;
}

/** Converte pontos em path SVG suave (Catmull-Rom → Bézier cúbica). */
const pathSuave = (pontos: { x: number; y: number }[]): string => {
    if (pontos.length < 2) return '';
    let d = `M ${pontos[0].x} ${pontos[0].y}`;
    for (let i = 0; i < pontos.length - 1; i++) {
        const p0 = pontos[Math.max(0, i - 1)];
        const p1 = pontos[i];
        const p2 = pontos[i + 1];
        const p3 = pontos[Math.min(pontos.length - 1, i + 2)];
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x} ${p2.y}`;
    }
    return d;
};

/**
 * No touch, `onMouseLeave` nunca dispara — quem toca uma barra/ponto do gráfico via
 * `onTouchStart` ficava com o tooltip preso na tela até tocar em outro ponto, sem jeito
 * de voltar ao estado padrão. Toque fora do gráfico (`ref`) limpa o "ativo".
 */
function useLimparAoTocarFora<T extends Element>(ativo: unknown, limpar: () => void) {
    const ref = useRef<T>(null);
    useEffect(() => {
        if (ativo === null || ativo === undefined) return;
        const aoTocar = (e: TouchEvent) => {
            if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) limpar();
        };
        document.addEventListener('touchstart', aoTocar);
        return () => document.removeEventListener('touchstart', aoTocar);
    }, [ativo, limpar]);
    return ref;
}

const GraficoGastos = ({ dados }: { dados: PontoGrafico[] }) => {
    const [ativo, setAtivo] = useState<number | null>(null);
    const svgRef = useLimparAoTocarFora<SVGSVGElement>(ativo, () => setAtivo(null));
    const { marca } = useMarcaPortal();
    const COR_SEM = marca.grafico.sem;
    const COR_SUPERFICIE = marca.grafico.superficie;

    const W = 340;
    const H = 150;
    const PAD_X = 22;
    const PAD_TOPO = 14;
    const ALTURA_PLOT = 100;

    // Escala a partir de zero: com o mínimo dos dados como base, a linha "com" virava o
    // próprio chão do gráfico e, em meses de valor parecido, as duas pareciam réguas
    // paradas sem informação nenhuma (visto na conta de teste da Gran, 29/09/2026).
    const valores = dados.flatMap((d) => [d.com, d.sem]);
    const min = 0;
    const max = Math.max(...valores) * 1.1 || 1;
    const faixa = max - min;
    const ultimo = dados.length - 1;
    const destaque = ativo ?? ultimo;

    const x = (i: number) => (dados.length === 1 ? W / 2 : PAD_X + (i * (W - 2 * PAD_X)) / (dados.length - 1));
    const y = (v: number) => PAD_TOPO + ALTURA_PLOT - ((v - min) / faixa) * ALTURA_PLOT;

    const pontosCom = dados.map((d, i) => ({ x: x(i), y: y(d.com) }));
    const pontosSem = dados.map((d, i) => ({ x: x(i), y: y(d.sem) }));

    return (
        <div className="relative">
            {/* Tooltip do mês ativo */}
            {ativo !== null && (
                <div
                    className="absolute -top-2 z-10 -translate-x-1/2 pointer-events-none bg-primary-dark/95 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
                    style={{ left: `${(x(ativo) / W) * 100}%` }}
                >
                    <p className="font-bold mb-1">{dados[ativo].mes}</p>
                    <p className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: COR_SEM }} />
                        {marca.economia.graficoSem}: {brl(dados[ativo].sem)}
                    </p>
                    <p className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: COR_COM }} />
                        {marca.economia.graficoCom}: {brl(dados[ativo].com)}
                    </p>
                </div>
            )}

            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" onMouseLeave={() => setAtivo(null)}>
                {/* grid vertical recessivo */}
                {dados.map((_, i) => (
                    <line
                        key={`grid-${i}`}
                        x1={x(i)} y1={PAD_TOPO} x2={x(i)} y2={PAD_TOPO + ALTURA_PLOT}
                        stroke="#FFFFFF" strokeOpacity={0.25} strokeWidth={1} strokeDasharray="2 5"
                    />
                ))}

                {/* linha de base (zero) */}
                <line x1={PAD_X} y1={PAD_TOPO + ALTURA_PLOT} x2={W - PAD_X} y2={PAD_TOPO + ALTURA_PLOT} stroke="#FFFFFF" strokeOpacity={0.3} strokeWidth={1} />

                {/* economia: faixa entre as duas linhas */}
                {dados.length > 1 && (
                    <path
                        d={`${pathSuave(pontosSem)} L ${pontosCom[ultimo].x} ${pontosCom[ultimo].y} ${pontosCom.slice().reverse().map((p) => `L ${p.x} ${p.y}`).join(' ')} Z`}
                        fill={COR_SEM} fillOpacity={0.12} stroke="none"
                    />
                )}

                {/* séries */}
                <path d={pathSuave(pontosSem)} fill="none" stroke={COR_SEM} strokeWidth={2.5} strokeLinecap="round" />
                <path d={pathSuave(pontosCom)} fill="none" stroke={COR_COM} strokeWidth={2.5} strokeLinecap="round" />

                {/* um ponto por mês; o mês em destaque (ativo, ou o último) fica maior */}
                {dados.map((d, i) => (
                    <g key={`pt-${i}`}>
                        <circle cx={x(i)} cy={y(d.sem)} r={i === destaque ? 4.5 : 2.5} fill={COR_SEM} stroke={COR_SUPERFICIE} strokeWidth={i === destaque ? 2 : 1} />
                        <circle cx={x(i)} cy={y(d.com)} r={i === destaque ? 4.5 : 2.5} fill={COR_COM} stroke={COR_SUPERFICIE} strokeWidth={i === destaque ? 2 : 1} />
                    </g>
                ))}

                {/* valores do último mês sempre à vista (sem precisar de hover) */}
                {ativo === null && (
                    <>
                        <text x={x(ultimo)} y={y(dados[ultimo].sem) - 8} textAnchor={dados.length > 1 ? 'end' : 'middle'} fontSize={10} fontWeight={700} fill="#FFFFFF">{brl(dados[ultimo].sem)}</text>
                        <text x={x(ultimo)} y={y(dados[ultimo].com) + 16} textAnchor={dados.length > 1 ? 'end' : 'middle'} fontSize={10} fontWeight={700} fill="#FFFFFF" fillOpacity={0.85}>{brl(dados[ultimo].com)}</text>
                    </>
                )}

                {/* rótulos dos meses */}
                {dados.map((d, i) => (
                    <text
                        key={`mes-${i}`}
                        x={x(i)} y={H - 6}
                        textAnchor="middle"
                        fontSize={11} fontWeight={700}
                        fill="#FFFFFF" fillOpacity={0.8}
                    >
                        {d.mes.split('/')[0]}
                    </text>
                ))}

                {/* alvos de hover/toque maiores que a marca */}
                {dados.map((_, i) => (
                    <rect
                        key={`hit-${i}`}
                        x={x(i) - (W - 2 * PAD_X) / (2 * Math.max(1, dados.length - 1))}
                        y={0}
                        width={(W - 2 * PAD_X) / Math.max(1, dados.length - 1)}
                        height={H}
                        fill="transparent"
                        onMouseEnter={() => setAtivo(i)}
                        onTouchStart={() => setAtivo(i)}
                    />
                ))}
            </svg>

            {/* legenda (identidade nunca só na cor) */}
            <div className="flex items-center justify-center gap-6 mt-1">
                <span className="flex items-center gap-2 text-xs font-bold tracking-wide text-white/80">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COR_COM }} />
                    {marca.economia.graficoCom.toUpperCase()}
                </span>
                <span className="flex items-center gap-2 text-xs font-bold tracking-wide text-white/80">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COR_SEM }} />
                    {marca.economia.graficoSem.toUpperCase()}
                </span>
            </div>
        </div>
    );
};

// ── Gráfico "Histórico de consumo" (barras, 1 série, superfície branca) ──────────
// Série única: sem legenda (o título nomeia); rótulo de valor só no mês ativo
// (hover/toque). Barras finas com topo arredondado, grid horizontal recessivo.

const GraficoConsumo = ({ dados }: { dados: { mes: string; kwh: number }[] }) => {
    const [ativo, setAtivo] = useState<number | null>(null);
    const svgRef = useLimparAoTocarFora<SVGSVGElement>(ativo, () => setAtivo(null));

    // Proporção larga (o cartão ocupa a largura toda no computador): com 340×160 o SVG
    // escalava ~3× e os nomes dos meses ficavam enormes.
    const W = 720;
    const H = 210;
    const PAD_X = 14;
    const PAD_TOPO = 28;
    const ALTURA_PLOT = 146;
    const BASE = PAD_TOPO + ALTURA_PLOT;

    const max = Math.max(...dados.map((d) => d.kwh)) || 1;
    const slot = (W - 2 * PAD_X) / dados.length;
    const larguraBarra = Math.min(48, slot * 0.55);

    // Retângulo com só o topo arredondado (4px), ancorado na linha de base
    const barra = (cx: number, altura: number): string => {
        const r = Math.min(4, altura);
        const x0 = cx - larguraBarra / 2;
        const x1 = cx + larguraBarra / 2;
        const yTopo = BASE - altura;
        return `M ${x0} ${BASE} L ${x0} ${yTopo + r} Q ${x0} ${yTopo} ${x0 + r} ${yTopo} L ${x1 - r} ${yTopo} Q ${x1} ${yTopo} ${x1} ${yTopo + r} L ${x1} ${BASE} Z`;
    };

    return (
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" onMouseLeave={() => setAtivo(null)}>
            {/* grid horizontal recessivo + linha de base */}
            {[0.5, 1].map((f) => (
                <line
                    key={f}
                    x1={PAD_X} y1={BASE - ALTURA_PLOT * f} x2={W - PAD_X} y2={BASE - ALTURA_PLOT * f}
                    stroke="#E5E7EB" strokeWidth={1} strokeDasharray="2 5"
                />
            ))}
            <line x1={PAD_X} y1={BASE} x2={W - PAD_X} y2={BASE} stroke="#E5E7EB" strokeWidth={1} />

            {dados.map((d, i) => {
                const cx = PAD_X + i * slot + slot / 2;
                const altura = Math.max(2, (d.kwh / max) * ALTURA_PLOT);
                return (
                    <g key={d.mes}>
                        <path d={barra(cx, altura)} fill={ativo === i ? '#212913' : '#5F6C37'} />
                        {/* kWh de cada mês sempre à vista: a diferença entre os meses é o que o gráfico conta */}
                        <text x={cx} y={BASE - altura - 7} textAnchor="middle" fontSize={11} fontWeight={700} fill={ativo === i ? '#111827' : '#6B7280'}>
                            {d.kwh.toLocaleString('pt-BR')} kWh
                        </text>
                        <text x={cx} y={H - 10} textAnchor="middle" fontSize={12} fontWeight={700} fill="#9CA3AF">
                            {d.mes.split('/')[0]}
                        </text>
                        {/* alvo de hover/toque maior que a marca */}
                        <rect
                            x={PAD_X + i * slot} y={0} width={slot} height={H}
                            fill="transparent"
                            onMouseEnter={() => setAtivo(i)}
                            onTouchStart={() => setAtivo(i)}
                        />
                    </g>
                );
            })}
        </svg>
    );
};

// ── Página ───────────────────────────────────────────────────────────────────────

const PortalConsumidorHome = () => {
    const { user, profile, loading, signOut } = useAuth();
    const { marca } = useMarcaPortal();
    const EMAIL_SUPORTE = marca.contato.email;
    // Comunicado da marca (ex.: o do Guilherme para a Gran) — cartão fixo para reler.
    const [comunicado, setComunicado] = useState<ComunicadoMarca | null>(null);
    const [comunicadoAberto, setComunicadoAberto] = useState(false);

    const [carregando, setCarregando] = useState(true);
    const [faturas, setFaturas] = useState<FaturaHome[]>([]);
    const [ucs, setUcs] = useState<UcHome[]>([]);
    const [parcelamentos, setParcelamentos] = useState<ParcelamentoHome[]>([]);
    const [quitacaoPorFatura, setQuitacaoPorFatura] = useState<Map<number, BoletoQuitacao>>(new Map());
    const [faturaPagamento, setFaturaPagamento] = useState<FaturaHome | null>(null);

    useTituloPortal('Portal do Consumidor');

    useEffect(() => {
        if (!user || profile?.role !== 'consumidor') return;
        const carregar = async () => {
            setCarregando(true);
            try {
                const [{ data: fat }, { data: unidades }, { data: acordos }, { data: boletos }] = await Promise.all([
                    supabase
                        .from('faturas')
                        .select('id, uc_codigo, mes_referencia, data_vencimento, vencimento_boleto, valor_fatura_distribuidora, valor_boleto_vigor, valor_emissao_boleto_unico, equatorial_quitada_pelo_cliente, economia, custo_s_vigor, total_com_vigor, consumo_ativo_kwh, status_boleto_vigor, codigo_pix_boleto, url_img_codigo_pix_boleto, linha_digitavel_boleto, url_boleto, url_boleto_combinado, pdf_fatura, created_at, cobranca_agrupada')
                        .is('excluido_em', null),
                    supabase
                        .from('unidades_consumidoras')
                        .select('id, numero_uc, endereco, logradouro, numero, bairro, cidade, estado, situacao, desconto_tarifa, concessionaria'),
                    supabase
                        .from('parcelamentos')
                        .select('id, descricao, valor_total, numero_parcelas, status, parcelamento_parcelas(status)')
                        .neq('status', 'cancelado')
                        .order('created_at', { ascending: false }),
                    supabase
                        .from('boletos_vigor')
                        .select('fatura_id, tem_desconto_quitacao, desc_quitacao_data_pgt_1, desc_quitacao_desconto_percent_1, desc_quitacao_desconto_valor_1, desc_quitacao_a_pagar_1, desc_quitacao_data_pgt_2, desc_quitacao_desconto_percent_2, desc_quitacao_desconto_valor_2, desc_quitacao_a_pagar_2, desc_quitacao_data_pgt_3, desc_quitacao_desconto_percent_3, desc_quitacao_desconto_valor_3, desc_quitacao_a_pagar_3')
                        .eq('tem_desconto_quitacao', true),
                ]);
                setFaturas((fat || []) as FaturaHome[]);
                setUcs((unidades || []) as UcHome[]);
                setParcelamentos((acordos || []) as ParcelamentoHome[]);
                const mapaQuitacao = new Map<number, BoletoQuitacao>();
                (boletos || []).forEach((b: BoletoQuitacao & { fatura_id: number }) => mapaQuitacao.set(b.fatura_id, b));
                setQuitacaoPorFatura(mapaQuitacao);
            } catch (err) {
                console.error('Erro ao carregar dados da home do portal:', err);
            } finally {
                setCarregando(false);
            }
        };
        carregar();
    }, [user, profile]);

    // A RLS já entrega só os avisos do público desta marca (avisos_consumidor.publico).
    useEffect(() => {
        if (!user || profile?.role !== 'consumidor') return;
        let vivo = true;
        supabase
            .from('avisos_consumidor')
            .select('id, titulo, mensagem, url_anexo, data_inicio')
            .eq('categoria', CATEGORIA_COMUNICADO)
            .order('data_inicio', { ascending: false })
            .limit(1)
            .then(({ data, error }) => {
                if (error) { console.error('Erro ao carregar comunicado do portal:', error); return; }
                if (vivo) setComunicado((data?.[0] as ComunicadoMarca) ?? null);
            });
        return () => { vivo = false; };
    }, [user, profile]);

    const resumo = useMemo(() => {
        // Ordena cronologicamente pelo mês de referência (fallback: vencimento)
        const ordenadas = [...faturas].sort((a, b) => {
            const oa = mesReferenciaParaOrdinal(a.mes_referencia);
            const ob = mesReferenciaParaOrdinal(b.mes_referencia);
            if (!Number.isNaN(oa) && !Number.isNaN(ob) && oa !== ob) return oa - ob;
            return (a.data_vencimento || '').localeCompare(b.data_vencimento || '');
        });

        const economiaAcumulada = ordenadas.reduce((soma, f) => soma + (economiaDaFatura(f) ?? 0), 0);

        const comEconomia = ordenadas.filter((f) => economiaDaFatura(f) !== null);
        const ultima = comEconomia[comEconomia.length - 1];
        const economiaMes = ultima ? economiaDaFatura(ultima) : null;
        const mesEconomia = ultima ? normalizeMesReferencia(ultima.mes_referencia).split('/')[0] : null;

        // Gráfico: últimos 6 meses com os dois valores disponíveis
        const grafico: PontoGrafico[] = ordenadas
            .map((f) => ({ mes: normalizeMesReferencia(f.mes_referencia), com: custoComVigor(f), sem: custoSemVigor(f) }))
            .filter((p): p is PontoGrafico => p.com !== null && p.sem !== null && p.mes.includes('/'))
            .slice(-6);

        const agora = new Date();
        const pendentes = ordenadas
            .filter((f) => STATUS_PENDENTES.includes(derivarStatusBoleto(f as any, agora)))
            .sort((a, b) => (a.vencimento_boleto || a.data_vencimento || '').localeCompare(b.vencimento_boleto || b.data_vencimento || ''));

        // Histórico de consumo: últimos 6 meses com kWh registrado
        const consumo = ordenadas
            .map((f) => ({ mes: normalizeMesReferencia(f.mes_referencia), kwh: f.consumo_ativo_kwh }))
            .filter((p): p is { mes: string; kwh: number } => typeof p.kwh === 'number' && p.kwh > 0 && p.mes.includes('/'))
            .slice(-6);

        return { economiaAcumulada, economiaMes, mesEconomia, grafico, pendentes, consumo };
    }, [faturas]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    const primeiroNome = profile?.full_name?.split(' ')[0] || 'consumidor';

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
                {/* Hero: economia + gráfico */}
                <div className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-3xl p-6 sm:p-10 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                        <div>
                            <h1 className="text-3xl sm:text-4xl font-bold">Olá, {primeiroNome}!</h1>
                            {carregando ? (
                                <div className="mt-6 flex items-center gap-2 text-white/70 text-sm">
                                    <Loader2 size={16} className="animate-spin" />
                                    Carregando sua economia...
                                </div>
                            ) : resumo.economiaMes ? (
                                <>
                                    <p className="mt-6 text-white/80 font-semibold uppercase text-sm tracking-wide">
                                        Você economizou em {resumo.mesEconomia}
                                    </p>
                                    <p className="text-4xl sm:text-5xl font-bold mt-1">{brl(resumo.economiaMes)}</p>
                                    {resumo.economiaAcumulada > 0 && (
                                        <p className="mt-4 text-white/80">
                                            Acumulando até hoje
                                            <span className="block text-xl font-bold text-white">{brl(resumo.economiaAcumulada)}</span>
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="mt-6 text-white/80 max-w-sm">
                                    Bem-vindo ao seu espaço na {marca.nome}. Sua economia aparecerá aqui a cada fatura processada.
                                </p>
                            )}
                        </div>

                        {resumo.grafico.length >= 2 && (
                            <div>
                                <p className="text-sm font-semibold text-white/80 mb-2">Gastos mensais</p>
                                <GraficoGastos dados={resumo.grafico} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Faturas pendentes */}
                <section>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-900">Faturas Pendentes</h2>
                        <Link to="/portal-consumidor/faturas" className="text-sm font-semibold text-primary hover:text-primary/80 flex items-center gap-1">
                            Ver todas
                            <ChevronRight size={16} />
                        </Link>
                    </div>

                    {carregando ? (
                        <div className="bg-white rounded-2xl border border-gray-100 p-8 flex justify-center">
                            <Loader2 size={24} className="animate-spin text-primary" />
                        </div>
                    ) : resumo.pendentes.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center text-sm text-gray-500">
                            Nenhuma fatura pendente no momento. 🎉
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {resumo.pendentes.slice(0, 2).map((fatura) => {
                                const vencido = (fatura.status_boleto_vigor || '').toLowerCase() === 'vencido';
                                return (
                                    <div key={fatura.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                        <div className="p-6">
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                <div>
                                                    <h3 className="font-bold text-gray-900 text-lg">
                                                        Referência {normalizeMesReferencia(fatura.mes_referencia)}
                                                    </h3>
                                                    <p className="text-xs text-gray-400 mt-0.5">UC {fatura.uc_codigo}</p>
                                                </div>
                                                <p className={`text-sm font-bold ${vencido ? 'text-red-600' : 'text-secondary'}`}>
                                                    Vencimento {dataBr(fatura.vencimento_boleto || fatura.data_vencimento)}
                                                </p>
                                            </div>
                                            <p className="text-4xl font-bold text-gray-900 mt-3 text-right">
                                                {brl(valorCobradoFatura(fatura) ?? fatura.valor_fatura_distribuidora)}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setFaturaPagamento(fatura)}
                                            className="w-full py-4 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest transition-colors"
                                        >
                                            PAGAR
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Histórico de consumo */}
                {resumo.consumo.length >= 2 && (
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-1">Histórico de consumo</h2>
                        <p className="text-sm text-gray-500 mb-4">Energia consumida por mês (kWh). Toque em um mês para ver o valor.</p>
                        <GraficoConsumo dados={resumo.consumo} />
                    </section>
                )}

                {/* Meus Parcelamentos */}
                {parcelamentos.length > 0 && (
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Meus Parcelamentos</h2>
                            <Link to="/portal-consumidor/parcelamentos" className="text-sm font-semibold text-primary hover:text-primary/80 flex items-center gap-1">
                                Ver detalhes
                                <ChevronRight size={16} />
                            </Link>
                        </div>
                        <div className="space-y-3">
                            {parcelamentos.slice(0, 2).map((acordo) => {
                                const total = acordo.numero_parcelas || acordo.parcelamento_parcelas.length || 1;
                                const pagas = acordo.parcelamento_parcelas.filter((p) => p.status === 'pago').length;
                                const progresso = Math.min(100, Math.round((pagas / total) * 100));
                                return (
                                    <Link
                                        key={acordo.id}
                                        to="/portal-consumidor/parcelamentos"
                                        className="block bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:border-primary/30 hover:shadow-md transition-all"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                                <CalendarClock size={22} className="text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <p className="font-bold text-gray-900 truncate">{acordo.descricao || 'Parcelamento'}</p>
                                                    <p className="text-sm font-bold text-gray-900">{brl(acordo.valor_total)} <span className="text-gray-400 font-normal">em {total}x</span></p>
                                                </div>
                                                <div className="flex items-center gap-3 mt-2.5">
                                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-primary rounded-full" style={{ width: `${progresso}%` }} />
                                                    </div>
                                                    <span className="text-xs font-semibold text-gray-500 shrink-0">{pagas}/{total} pagas</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Comunicado da marca (#6117): abre sozinho uma vez pelo Gate; aqui fica para reler. */}
                {comunicado && (
                    <button
                        type="button"
                        onClick={() => setComunicadoAberto(true)}
                        className="w-full text-left bg-white rounded-2xl border border-primary/20 shadow-sm p-5 flex items-center gap-4 hover:border-primary/40 hover:shadow-md transition-all group"
                    >
                        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Megaphone size={22} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900">{comunicado.titulo}</p>
                            <p className="text-sm text-gray-500">Seu contrato e seus benefícios não mudam. Toque para ler o comunicado.</p>
                        </div>
                        <ChevronRight size={18} className="text-gray-300 group-hover:text-primary transition-colors shrink-0" />
                    </button>
                )}

                {/* Boleto único (#5575): convite fixo e situação da adesão. Some sozinho para quem
                    não tem UC da Vigor que possa aderir — e para o cliente de parceira, que já é
                    100% boleto único (#6117). */}
                {marca.mostrarConviteBoletoUnico && <CartaoBoletoUnico />}

                {/* Minhas Unidades */}
                <section>
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Minhas Unidades</h2>
                    {carregando ? null : ucs.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-sm text-gray-500">
                            Nenhuma unidade consumidora vinculada ao seu CPF ainda. Fale com o{' '}
                            <a href={`mailto:${EMAIL_SUPORTE}`} className="font-semibold text-primary">atendimento</a>{' '}
                            se algo estiver faltando.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {ucs.map((uc) => (
                                <div key={uc.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                        <Home size={22} className="text-primary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-bold text-gray-900">UC {uc.numero_uc}</p>
                                        <p className="text-sm text-gray-500 truncate">{enderecoDaUc(uc)}</p>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {typeof uc.desconto_tarifa === 'number' && uc.desconto_tarifa > 0 && (
                                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
                                                    <BadgePercent size={13} />
                                                    {uc.concessionaria || 'Equatorial'} · {Math.round(uc.desconto_tarifa * 100)}% de desconto
                                                </span>
                                            )}
                                            {uc.situacao && (
                                                <span className="inline-flex items-center text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-3 py-1">
                                                    {uc.situacao}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Ações rápidas */}
                <section className={`grid grid-cols-1 gap-4 ${marca.mostrarIndicar ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                    <Link
                        to="/portal-consumidor/faturas"
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col hover:border-primary/30 hover:shadow-md transition-all group"
                    >
                        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                            <FileText size={22} className="text-primary" />
                        </div>
                        <h3 className="font-bold text-gray-900">Minhas Faturas</h3>
                        <p className="text-sm text-gray-500 mt-1 flex-1">Histórico completo, boletos e 2ª via.</p>
                        <ChevronRight size={18} className="mt-3 self-end text-gray-300 group-hover:text-primary transition-colors" />
                    </Link>

                    <Link
                        to="/portal-consumidor/ajuda"
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col hover:border-primary/30 hover:shadow-md transition-all group"
                    >
                        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                            <BookOpen size={22} className="text-primary" />
                        </div>
                        <h3 className="font-bold text-gray-900">Ajuda</h3>
                        <p className="text-sm text-gray-500 mt-1 flex-1">Dúvidas frequentes sobre sua assinatura, fatura e desconto.</p>
                        <ChevronRight size={18} className="mt-3 self-end text-gray-300 group-hover:text-primary transition-colors" />
                    </Link>

                    {marca.mostrarIndicar && (
                    <a
                        href={`https://wa.me/?text=${encodeURIComponent('Estou economizando na conta de luz com energia limpa da Vigor Energy! Conheça: https://www.vigorenergy.com.br')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col hover:border-primary/30 hover:shadow-md transition-all group"
                    >
                        <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                            <Gift size={22} className="text-secondary" />
                        </div>
                        <h3 className="font-bold text-gray-900">Indicar</h3>
                        <p className="text-sm text-gray-500 mt-1 flex-1">Compartilhe a Vigor Energy com amigos e familiares.</p>
                        <ChevronRight size={18} className="mt-3 self-end text-gray-300 group-hover:text-secondary transition-colors" />
                    </a>
                    )}
                </section>

                {/* Lembretes de vencimento no WhatsApp */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
                        <BellRing size={22} className="text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900">Lembretes no WhatsApp</p>
                        {profile?.whatsapp ? (
                            <p className="text-sm text-gray-500 mt-0.5">
                                Enviamos avisos de vencimento das suas faturas para{' '}
                                <span className="font-semibold text-gray-700">{formatWhatsapp(profile.whatsapp)}</span>.
                            </p>
                        ) : (
                            <p className="text-sm text-gray-500 mt-0.5">
                                Cadastre seu WhatsApp para receber os avisos de vencimento das suas faturas.
                            </p>
                        )}
                    </div>
                    <Link
                        to="/portal-consumidor/perfil"
                        className="shrink-0 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                    >
                        {profile?.whatsapp ? 'Alterar' : 'Cadastrar'}
                    </Link>
                </div>

                {/* Atendimento */}
                <a
                    href={linkWhatsapp(marca, marca.saudacaoWhatsapp.replace('tenho uma dúvida', 'preciso de atendimento'))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 bg-primary/5 border border-primary/15 rounded-2xl px-6 py-4 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors"
                >
                    <MessageCircle size={18} />
                    Falar com o atendimento no WhatsApp
                    <Headset size={18} />
                </a>
            </main>

            {comunicadoAberto && comunicado && (
                <ComunicadoMarcaModal comunicado={comunicado} onFechar={() => setComunicadoAberto(false)} />
            )}

            {faturaPagamento && (
                <PagamentoModal
                    fatura={faturaPagamento}
                    quitacao={quitacaoPorFatura.get(faturaPagamento.id)}
                    onClose={() => setFaturaPagamento(null)}
                />
            )}
        </div>
    );
};

export default PortalConsumidorHome;
